import interact from "interactjs";
import {
  QuestionIcon,
  DeleteIcon,
  AddIcon,
  DragHandleIcon,
} from "../../../editorjs/icons/icons";

import BtnTooltip from "../../tooltips/btnTooltip";

export default class Question {
  static get toolbox() {
    return {
      icon: QuestionIcon,
      title: "Question",
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  constructor({ data, config, api, readOnly, block }) {
    this.api = api;
    this.readOnly = readOnly;
    this.block = block;

    this._CSS = {
      baseClass: this.api.styles.block,
      loading: this.api.styles.loader,
      settingsButton: this.api.styles.settingsButton,
      settingsButtonActive: this.api.styles.settingsButtonActive,

      /**
       * Tool's classes
       */
      mainQuestionContainer: "main-question-container",
      questionInput: "question-input",

      typeContainer: "question-type-container",
      typeLabel: "question-type-label",
      typeSelect: "question-type-select",
      typeOption: "question-type-option",

      optionsContainer: "question-options-container",
      addOptionBtn: "add-option-btn",

      optionItem: "question-option-item",
      optionInput: "question-option-input",
      optionDragBtn: "question-option-drag-btn",
      optionDragging: "is-dragging",
      removeOptionBtn: "remove-option-btn",
      correctOptionCheckbox: "correct-option-checkbox",

      blockBtn: "block-btn",
      blockBtnSmall: "small",
      blockBtnLarge: "large",
      largeIcon: "large-icon",
      xlIcon: "xl-icon",
      xxlIcon: "xxl-icon",
      clear: "clear",
      focusVisibleBtn: "focus-visible-btn",
      tuneWrapper: "editor-block-tune",
      tuneTitle: "editor-block-tune-title",
      tuneText: "editor-block-tune-text",
      tuneInput: "editor-block-tune-input",
      editorFont: "editor-font",
      editorBlockModal: "editor-block-modal",
      editorBlockModalTitle: "editor-block-modal-title",
      editorBlockModalLabel: "editor-block-modal-label",
      editorBlockModalText: "editor-block-modal-text",
      editorBlockModalInput: "editor-block-modal-input",
      adminFont: "admin-font",
    };

    /**
     * Tool's settings passed from Editor
     *
     * @type {TitleConfig}
     * @private
     */
    this._settings = config;

    this._data = {
      field_answers: data.field_answers || [
        {
          field_answer: "Option 1",
          field_correct: true,
          id: `option-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        },
        {
          field_answer: "Option 2",
          field_correct: false,
          id: `option-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        },
      ],
      field_question: data.field_question || "Question text",
      type: data.type || "Single answer",
    };
    this._container = "";

    this.optionsContainer = null;
  }

  questionOption(_optionData) {
    const css = this._CSS;
    const optionId =
      _optionData?.id ||
      `option-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const optionItem = document.createElement("div");
    const optionInput = document.createElement("p");
    const dragHandle = document.createElement("button");
    const removeOptionBtn = document.createElement("button");
    const correctOptionCheckbox = document.createElement("input");

    optionItem.setAttribute("data-option-id", optionId);
    optionItem.classList.add(css.optionItem);
    optionInput.classList.add(css.optionInput);
    dragHandle.classList.add(
      css.optionDragBtn,
      css.blockBtn,
      css.blockBtnSmall,
    );
    removeOptionBtn.classList.add(
      css.blockBtn,
      css.blockBtnSmall,
      css.xlIcon,
      css.clear,
      css.removeOptionBtn,
    );
    correctOptionCheckbox.classList.add(css.correctOptionCheckbox);

    optionInput.placeholder = "Option text...";

    dragHandle.innerHTML = DragHandleIcon;
    removeOptionBtn.innerHTML = DeleteIcon;
    BtnTooltip(dragHandle, "Drag option");
    BtnTooltip(removeOptionBtn, "Remove this option");
    correctOptionCheckbox.type = "checkbox";
    BtnTooltip(correctOptionCheckbox, "Mark as correct answer");

    if (_optionData) {
      optionInput.innerHTML = _optionData.field_answer;
      correctOptionCheckbox.checked = _optionData.field_correct;
    }

    if (this.readOnly) {
      correctOptionCheckbox.setAttribute("disabled", "disabled");
      removeOptionBtn.setAttribute("disabled", "disabled");
    }

    if (!this.readOnly) {
      optionItem.appendChild(dragHandle);
    }
    optionItem.appendChild(optionInput);

    if (!this.readOnly) {
      optionInput.setAttribute("contenteditable", "true");

      removeOptionBtn.addEventListener("click", () => {
        optionItem.remove();
        this._data.field_answers = this._data.field_answers.filter(
          (opt) => opt.element !== optionItem,
        );
      });

      correctOptionCheckbox.addEventListener("change", (event) => {
        if (this._data.type === "Single answer" && event.target.checked) {
          const optionItems = this.optionsContainer.querySelectorAll(
            `.${this._CSS.optionItem}`,
          );
          optionItems.forEach((item) => {
            const checkbox = item.querySelector(
              `.${this._CSS.correctOptionCheckbox}`,
            );
            if (checkbox !== event.target) {
              checkbox.checked = false;
            }
          });
        }
        setTimeout(() => {
          this.block.dispatchChange();
        }, 300);
      });

      optionItem.appendChild(correctOptionCheckbox);
      optionItem.appendChild(removeOptionBtn);
    }

    return optionItem;
  }

  /**
   * Drag-to-reorder options using interact.js.
   * Delegated to the options container so options added later via
   * "Add option" are draggable automatically. save() reads the DOM order,
   * so no data reshuffling is needed on drop.
   */
  initSortable(container) {
    const css = this._CSS;
    let dy = 0;

    this._interactable = interact(`.${css.optionItem}`, {
      context: container,
    }).draggable({
      allowFrom: `.${css.optionDragBtn}`,
      autoScroll: true,
      listeners: {
        start: (event) => {
          dy = 0;
          event.target.classList.add(css.optionDragging);
        },

        move: (event) => {
          const item = event.target;
          dy += event.dy;
          item.style.transform = `translateY(${dy}px)`;

          const swapWith = (sibling, before) => {
            const topBefore = item.getBoundingClientRect().top;
            container.insertBefore(
              before ? item : sibling,
              before ? sibling : item,
            );
            const topAfter = item.getBoundingClientRect().top;
            // Keep the item visually under the pointer after its layout slot changes
            dy += topBefore - topAfter;
            item.style.transform = `translateY(${dy}px)`;
          };

          const rect = item.getBoundingClientRect();
          const center = rect.top + rect.height / 2;

          const next = item.nextElementSibling;
          if (next) {
            const r = next.getBoundingClientRect();
            if (center > r.top + r.height / 2) return swapWith(next, false);
          }

          const prev = item.previousElementSibling;
          if (prev) {
            const r = prev.getBoundingClientRect();
            if (center < r.top + r.height / 2) return swapWith(prev, true);
          }
        },

        end: (event) => {
          const item = event.target;
          item.classList.remove(css.optionDragging);
          item.style.transform = "";
          this.block.dispatchChange();
        },
      },
    });
  }

  questionOptions() {
    const css = this._CSS;
    const optionsContainer = document.createElement("div");
    optionsContainer.classList.add(css.optionsContainer);
    if (this._data.field_answers.length > 0) {
      this._data.field_answers.forEach((option) => {
        const optionElement = this.questionOption(option);
        optionsContainer.appendChild(optionElement);
      });
    }

    if (!this.readOnly) this.initSortable(optionsContainer);

    return optionsContainer;
  }

  questionType() {
    const css = this._CSS;
    const typeContainer = document.createElement("div");
    const typeLabel = document.createElement("label");
    const typeSelect = document.createElement("select");
    const typeOptions = ["Single answer", "Multiple answer"];

    typeContainer.classList.add(css.typeContainer);
    typeLabel.classList.add(css.typeLabel);
    typeSelect.classList.add(css.typeSelect);

    typeOptions.forEach((option) => {
      const optionElement = document.createElement("option");
      optionElement.classList.add(css.typeOption);
      optionElement.value = option;
      optionElement.textContent = option;
      typeSelect.appendChild(optionElement);
    });

    typeSelect.value = this._data.type;

    typeSelect.addEventListener("change", (event) => {
      this._data.type = event.target.value;
      if (this._data.type === "Single answer") {
        const optionItems = this.optionsContainer.querySelectorAll(
          `.${this._CSS.optionItem}`,
        );
        optionItems.forEach((item) => {
          const checkbox = item.querySelector(
            `.${this._CSS.correctOptionCheckbox}`,
          );
          if (checkbox !== event.target) {
            checkbox.checked = false;
          }
        });
      }
      setTimeout(() => {
        this.block.dispatchChange();
      }, [300]);
    });

    typeLabel.textContent = "Answer Type:";
    typeLabel.setAttribute("for", "question-type-select");
    typeSelect.id = "question-type-select";
    typeContainer.appendChild(typeLabel);
    typeContainer.appendChild(typeSelect);
    return typeContainer;
  }

  questionMain() {
    const css = this._CSS;
    const container = document.createElement("div");
    const questionInput = document.createElement("h5");

    const addOptionBtn = document.createElement("button");

    addOptionBtn.innerHTML = `${AddIcon} Add option`;
    addOptionBtn.classList.add(
      css.blockBtn,
      css.blockBtnLarge,
      css.addOptionBtn,
    );
    addOptionBtn.addEventListener("click", () => {
      this.optionsContainer.appendChild(this.questionOption());
    });
    questionInput.classList.add(css.questionInput);
    container.classList.add(css.mainQuestionContainer);

    if (!this.readOnly) {
      questionInput.setAttribute("contenteditable", "true");
    }

    if (this._data.field_question) {
      questionInput.innerHTML = this._data.field_question;
    }

    questionInput.placeholder = "Type your question here...";

    this.optionsContainer = this.questionOptions();

    container.appendChild(questionInput);
    container.appendChild(this.questionType());
    container.appendChild(this.optionsContainer);
    if (!this.readOnly) {
      container.appendChild(addOptionBtn);
    }
    return container;
  }

  render() {
    this._container = document.createElement("div");
    this._container.appendChild(this.questionMain());
    return this._container;
  }

  save() {
    const questionInput = this._container.querySelector(
      `.${this._CSS.questionInput}`,
    );
    const typeSelect = this._container.querySelector(
      `.${this._CSS.typeSelect}`,
    );
    const optionItems = this._container.querySelectorAll(
      `.${this._CSS.optionItem}`,
    );
    const options = [];

    optionItems.forEach((item) => {
      const optionInput = item.querySelector(`.${this._CSS.optionInput}`);
      const correctOptionCheckbox = item.querySelector(
        `.${this._CSS.correctOptionCheckbox}`,
      );
      options.push({
        field_answer: optionInput.innerHTML,
        field_correct: correctOptionCheckbox.checked,
        id: item.getAttribute("data-option-id"),
      });
    });

    return {
      field_question: questionInput.innerHTML,
      type: typeSelect.value,
      field_answers: options,
    };
  }

  destroy() {
    if (this._interactable) this._interactable.unset();
  }
}